import React, { useEffect, useState } from 'react';
import { HttpProvider, UserProvider } from '@exerai/react-core';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { ErrorBoundary } from '@sentry/react';
import { Amplify, Auth } from 'aws-amplify';
import { RecoilRoot, useRecoilSnapshot } from 'recoil';
import { config } from '@/common/amplify';
import { ProfileProperties } from '@/common/types';
import { refreshUserProfile } from '@/common/utils';
import { ErrorHandler } from '@/components/common/ErrorBoundary/ErrorHandler';
import { ExercisesProvider } from '@/context/ExercisesContext';
import { ModalProvider } from '@/context/ModalContext';
import { HttpService } from '@/services/HttpService';
import { exerTheme } from '@/theme/exerTheme';
import App from './App';
import { AssessmentsProvider } from './context/AssessmentsContext';

Amplify.configure(config);

type HttpServiceCtor = new (...args: never[]) => unknown;

const useHttpServiceClass = (): HttpServiceCtor | null => {
    const [serviceClass, setServiceClass] = useState<HttpServiceCtor | null>(() => {
        if (process.env.MOCK_HTTP === 'true') return null;
        return HttpService as unknown as HttpServiceCtor;
    });

    useEffect(() => {
        if (process.env.MOCK_HTTP !== 'true') return;
        import(/* webpackChunkName: "http-service-mock" */ '@/services/HttpServiceMock').then((mod) => {
            setServiceClass(() => mod.HttpService as unknown as HttpServiceCtor);
        });
    }, []);

    return serviceClass;
};

const DebugObserver = () => {
    const snapshot = useRecoilSnapshot();
    useEffect(() => {
        console.debug('The following atoms were modified:');
        for (const node of snapshot.getNodes_UNSTABLE({ isModified: true })) {
            console.debug(node.key, snapshot.getLoadable(node));
        }
    }, [snapshot]);

    return null;
};

const AppProvider = () => {
    const httpServiceClass = useHttpServiceClass();

    if (!httpServiceClass) return null;

    return (
        <>
            <ModalProvider>
                <HttpProvider HttpService={httpServiceClass}>
                    <RecoilRoot>
                        <UserProvider
                            Auth={Auth}
                            refreshUserProfile={refreshUserProfile}
                            additionalProperties={ProfileProperties}
                        >
                            <ExercisesProvider>
                                <AssessmentsProvider>
                                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                                        <ThemeProvider theme={exerTheme}>
                                            {process.env.ENVIRONMENT !== 'DEVELOPMENT' ? (
                                                <ErrorBoundary fallback={ErrorHandler}>
                                                    <App />
                                                </ErrorBoundary>
                                            ) : (
                                                <>
                                                    <DebugObserver />
                                                    {/*<React.StrictMode>*/}
                                                    <App />
                                                    {/*</React.StrictMode>*/}
                                                </>
                                            )}
                                        </ThemeProvider>
                                    </LocalizationProvider>
                                </AssessmentsProvider>
                            </ExercisesProvider>
                        </UserProvider>
                    </RecoilRoot>
                </HttpProvider>
            </ModalProvider>
        </>
    );
};

export default AppProvider;
